import type { ReactNode } from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Select, TextField } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Call02Icon,
  Edit04Icon,
  Link01Icon,
  Location06Icon,
  Mail01Icon,
  UserAccountIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import ContactType from "enums/ContactType";
import MerchantSearchType from "enums/MerchantSearchType";
import RelationshipType from "enums/RelationshipType";
import MerchantSearchAutoComplete, {
  merchantForDomain,
} from "components/common/MerchantSearchAutoComplete";
import { formatUrl } from "components/common/RenderMerchantDisplay";
import { EMPTY_CONTACT_ADDRESS, FormikContact } from "hooks/useContactData";
import Summary from "components/common/Summary";
import FormikCustomFields from "components/common/FormikCustomFields";
import CustomFieldParentType from "enums/CustomFieldParentType";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import ValueWithIcon from "components/common/contacts/ValueWithIcon";
import { relationshipLabelKey } from "utils/contacts/relationship";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import AddressField from "components/common/address/AddressField";

/** The flat text fields this card edits, so a formik error is a string. */
type EditableTextField =
  | "businessName"
  | "firstName"
  | "lastName"
  | "title"
  | "email"
  | "phone"
  | "website";

interface ContactDetailsCardProps {
  formik: FormikProps<FormikContact>;
  editing: boolean;
  onOpenAddressDrawer: () => void;
}

const ContactDetailsCard: React.FC<ContactDetailsCardProps> = ({
  formik,
  editing,
  onOpenAddressDrawer,
}) => {
  const { t } = useTranslation();

  const contact = formik.values;
  const isBusiness = contact.contactType === ContactType.Business;
  const location = contact.address?.city
    ? `${contact.address.city}, ${contact.address.state}`
    : "";

  const { customFieldSettings, filteredCustomFieldSummaryItems } =
    useCustomFieldRenderer({
      parentType: CustomFieldParentType.CONTACT,
      customFields: contact.customFields,
    });

  const errorFor = (name: EditableTextField) =>
    formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  const field = (
    name: EditableTextField,
    label: string,
    value: string,
    required = false,
  ) => (
    <TextField
      key={name}
      fullWidth
      required={required}
      label={label}
      name={name}
      value={value}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      errorText={errorFor(name)}
    />
  );

  const locationField = (
    <AddressField
      key="location"
      label={t("Location")}
      address={contact.address}
      onOpen={onOpenAddressDrawer}
      onClear={() => formik.setFieldValue("address", EMPTY_CONTACT_ADDRESS)}
      trailing={<HugeiconsIcon icon={Location06Icon} size={16} />}
    />
  );

  // The same domain search the business drawer edits with. Unlike the drawer,
  // clearing it leaves the business name alone.
  const websiteField = (
    <MerchantSearchAutoComplete
      key="website"
      required
      title={t("Website")}
      placeholder={t("Search Business Domain")}
      merchant={contact.website ? merchantForDomain(contact.website) : null}
      onSelectMerchant={(merchant) =>
        formik.setFieldValue("website", merchant?.url.toLowerCase() ?? "")
      }
      allowUseSearchValue
      type={MerchantSearchType.brand}
      errorText={errorFor("website")}
      handleBlur={() => formik.setFieldTouched("website", true)}
    />
  );

  const relationshipField = (
    <Select
      key="relationshipType"
      fullWidth
      required
      // Not clearable, and the handler ignores a null anyway. The field is
      // RelationshipTypeEnum! on the server, and updateContact spreads it as
      // `...(relationshipType && {...})`, so a cleared one is dropped from the
      // mutation: the record keeps its old value while the form shows empty.
      clearable={false}
      label={t("Relationship")}
      value={contact.relationshipType ?? null}
      options={Object.values(RelationshipType).map((type) => ({
        id: type,
        label: t(relationshipLabelKey(type) ?? type),
      }))}
      onChange={(value) =>
        value && formik.setFieldValue("relationshipType", value)
      }
    />
  );

  const editFields: ReactNode[] = isBusiness
    ? [
        field("businessName", t("Company"), contact.businessName ?? "", true),
        websiteField,
        field("phone", t("Phone"), contact.phone ?? "", true),
        field("email", t("Email"), contact.email ?? "", true),
        locationField,
        relationshipField,
      ]
    : [
        // Two name fields rather than the design's one: the record stores
        // firstName and lastName, and splitting a typed full name back into
        // them guesses wrong on any name with more than two parts.
        field("firstName", t("First Name"), contact.firstName ?? ""),
        field("lastName", t("Last Name"), contact.lastName ?? ""),
        field("title", t("Title"), contact.title ?? ""),
        field("email", t("Email"), contact.email ?? "", true),
        field("phone", t("Phone"), contact.phone ?? "", true),
        locationField,
        relationshipField,
      ];

  const viewItems = [
    {
      title: t("Email"),
      value: <ValueWithIcon icon={Mail01Icon}>{contact.email}</ValueWithIcon>,
    },
    {
      title: t("Phone"),
      value: <ValueWithIcon icon={Call02Icon}>{contact.phone}</ValueWithIcon>,
    },
    {
      title: t("Location"),
      value: <ValueWithIcon icon={Location06Icon}>{location}</ValueWithIcon>,
    },
    // A business has a website where a person has a job title.
    ...(isBusiness
      ? [
          {
            title: t("Website"),
            value: (
              <ValueWithIcon icon={Link01Icon}>
                {contact.website && (
                  <Link
                    href={formatUrl(contact.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {contact.website}
                  </Link>
                )}
              </ValueWithIcon>
            ),
          },
        ]
      : [
          {
            title: t("Title"),
            value: (
              <ValueWithIcon icon={UserAccountIcon}>
                {contact.title}
              </ValueWithIcon>
            ),
          },
        ]),
    ...filteredCustomFieldSummaryItems,
  ];

  return (
    <Card variant="outlined" sx={{ padding: "var(--space-6)" }}>
      <Stack gap="var(--space-5)">
        <Inline justify="between" align="center">
          <Typography variant="h5">{t("Details")}</Typography>
        </Inline>
        {editing ? (
          <Stack gap="var(--space-5)">
            <Columns count={2} gap="var(--space-4)">
              {editFields}
            </Columns>
            <FormikCustomFields
              formik={formik}
              settings={customFieldSettings}
              variant="flat"
              columns={2}
              sx={{ gap: "var(--space-4)" }}
            />
          </Stack>
        ) : (
          <Summary items={viewItems} disableContainer />
        )}
      </Stack>
    </Card>
  );
};

export default ContactDetailsCard;
